import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { baseForBooking, applyDiscount, SERVICE_PRICES } from "@/lib/pricing";
import { CalendarCheck, Loader2, Tag } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIME_SLOTS = ["09:00", "10:30", "12:00", "13:30", "15:00", "16:30"];

const inputCls =
  "bg-[#0E1015] border-[#262933] focus-visible:ring-nitro text-neutral-100 h-11";

export const BookingModal = ({ state, onOpenChange }) => {
  const { open, vehicle, stage, promo: promoParam } = state;
  const [sending, setSending] = useState(false);
  const [slots, setSlots] = useState(TIME_SLOTS);
  const [blockedDays, setBlockedDays] = useState([]);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    preferred_date: "",
    time_slot: "",
    service: "",
    registration: "",
    message: "",
  });

  useEffect(() => {
    if (open) {
      setSending(false);
      setPromo(null);
      setPromoInput("");
      setPromoError("");
      setForm((f) => ({
        ...f,
        message: stage ? `I'm interested in the ${stage} package.` : "",
      }));
      axios
        .get(`${API}/settings/time-slots`)
        .then((r) => setSlots(r.data.slots?.length ? r.data.slots : TIME_SLOTS))
        .catch(() => setSlots(TIME_SLOTS));
      axios
        .get(`${API}/settings/blocked-days`)
        .then((r) => setBlockedDays(r.data.days || []))
        .catch(() => setBlockedDays([]));
      if (promoParam) applyPromo(promoParam);
    }
  }, [open, stage, promoParam]);

  const isNotListed = !!vehicle && vehicle.startsWith("Vehicle not listed");
  const showService = !stage && (!vehicle || isNotListed);
  const basePrice = baseForBooking(isNotListed ? null : vehicle, stage || form.service || null);
  const totalPrice = basePrice != null ? applyDiscount(basePrice, promo) : null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const pickDate = (e) => {
    const v = e.target.value;
    if (v && blockedDays.includes(v)) {
      toast.error("That day is unavailable — please pick another date");
      setForm((f) => ({ ...f, preferred_date: "" }));
      return;
    }
    setForm((f) => ({ ...f, preferred_date: v }));
  };

  const applyPromo = async (codeOverride) => {
    const v = (typeof codeOverride === "string" ? codeOverride : promoInput).trim();
    if (!v) return;
    setPromoInput(v.toUpperCase());
    try {
      const { data } = await axios.get(`${API}/promo/validate`, { params: { code: v } });
      if (data.valid) {
        setPromo({
          code: data.code,
          note: data.note,
          discount_type: data.discount_type || "",
          discount_value: data.discount_value || 0,
        });
        setPromoError("");
        toast.success(`Promo applied: ${data.code}${data.note ? ` — ${data.note}` : ""}`);
      } else {
        setPromo(null);
        setPromoError("Code not recognised");
      }
    } catch {
      setPromoError("Could not check code — try again");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (blockedDays.includes(form.preferred_date)) {
      toast.error("That day is unavailable — please pick another date");
      return;
    }
    if (!form.time_slot) {
      toast.error("Please pick a time slot");
      return;
    }
    if (showService && !form.service) {
      toast.error("Please choose a service");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...form,
        vehicle: isNotListed
          ? form.service
            ? `Vehicle not listed — ${form.service}`
            : vehicle
          : vehicle ||
            (stage
              ? `General enquiry — ${stage}`
              : form.service
                ? `General enquiry — ${form.service}`
                : "General enquiry"),
        promo_code: promo?.code || "",
      });
      toast.success("Booking request received", {
        description: "We will confirm your appointment shortly. Get ready for more power.",
      });
      onOpenChange(false);
      setForm({ full_name: "", phone: "", email: "", preferred_date: "", time_slot: "", service: "", registration: "", message: "" });
      setPromo(null);
      setPromoInput("");
    } catch (err) {
      toast.error("Booking failed", {
        description: "Something went wrong — please try again or call the garage.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-modal"
        className="bg-panel border-[#262933] text-neutral-100 sm:max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-nitro">
            Book Appointment
          </p>
          <DialogTitle className="font-heading font-bold uppercase text-3xl tracking-wide text-neutral-100">
            Lock in your remap
          </DialogTitle>
          <DialogDescription className="text-neutral-400 text-sm">
            Pick a date and slot — we confirm every booking personally.
          </DialogDescription>
        </DialogHeader>

        {vehicle && (
          <div
            data-testid="booking-vehicle-summary"
            className="rounded-lg border border-nitro/40 bg-nitro/10 px-4 py-3 flex items-center gap-3"
          >
            <CalendarCheck size={18} className="text-nitro shrink-0" />
            <p className="font-mono text-xs text-neutral-200 leading-relaxed">{vehicle}</p>
          </div>
        )}

        <form data-testid="booking-form" onSubmit={submit} className="flex flex-col gap-4 mt-2">
          {showService && (
            <div>
              <Label className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500 block mb-2">
                Service
              </Label>
              <Select
                value={form.service}
                onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}
              >
                <SelectTrigger
                  data-testid="booking-service-select"
                  className={`${inputCls} h-11`}
                >
                  <SelectValue placeholder="Choose a service" />
                </SelectTrigger>
                <SelectContent className="bg-panel border-[#262933] text-neutral-100">
                  {Object.entries(SERVICE_PRICES).map(([name, price]) => (
                    <SelectItem
                      key={name}
                      value={name}
                      data-testid={`service-option-${name.toLowerCase().replace(/\s+/g, "-")}`}
                      className="font-heading text-base focus:bg-nitro/20 focus:text-white"
                    >
                      {name} — £{price}
                    </SelectItem>
                  ))}
                  <SelectItem
                    value="General Enquiry"
                    data-testid="service-option-general-enquiry"
                    className="font-heading text-base focus:bg-nitro/20 focus:text-white"
                  >
                    General Enquiry — price on request
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label htmlFor="bk-reg" className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500">
              Vehicle Registration (optional)
            </Label>
            <Input
              id="bk-reg"
              data-testid="booking-registration-input"
              value={form.registration}
              onChange={(e) =>
                setForm((f) => ({ ...f, registration: e.target.value.toUpperCase() }))
              }
              placeholder="e.g. BW19 MRT"
              maxLength={8}
              className={`${inputCls} mt-1.5 uppercase font-mono tracking-[0.15em]`}
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="bk-name" className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500">
                Full Name
              </Label>
              <Input
                id="bk-name"
                data-testid="booking-name-input"
                required
                value={form.full_name}
                onChange={set("full_name")}
                placeholder="John Smith"
                className={inputCls}
              />
            </div>
            <div>
              <Label htmlFor="bk-phone" className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500">
                Phone
              </Label>
              <Input
                id="bk-phone"
                data-testid="booking-phone-input"
                required
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                placeholder="07123 456789"
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="bk-email" className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500">
              Email
            </Label>
            <Input
              id="bk-email"
              data-testid="booking-email-input"
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="john@example.com"
              className={inputCls}
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="bk-date" className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500">
                Preferred Date
              </Label>
              <Input
                id="bk-date"
                data-testid="booking-date-input"
                required
                type="date"
                min={new Date().toISOString().split("T")[0]}
                value={form.preferred_date}
                onChange={pickDate}
                className={inputCls}
              />
            </div>
            <div>
              <Label className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500 block mb-2">
                Time Slot
              </Label>
              <Select value={form.time_slot} onValueChange={(v) => setForm((f) => ({ ...f, time_slot: v }))}>
                <SelectTrigger data-testid="booking-timeslot-select" className={`${inputCls} h-11`}>
                  <SelectValue placeholder="Pick a slot" />
                </SelectTrigger>
                <SelectContent className="bg-panel border-[#262933] text-neutral-100">
                  {slots.map((t) => (
                    <SelectItem key={t} value={t} className="font-mono focus:bg-nitro/20 focus:text-white">
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="bk-msg" className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500">
              Message (optional)
            </Label>
            <Textarea
              id="bk-msg"
              data-testid="booking-message-input"
              rows={3}
              value={form.message}
              onChange={set("message")}
              placeholder="Anything we should know — mods, fuel grade, goals…"
              className="bg-[#0E1015] border-[#262933] focus-visible:ring-nitro text-neutral-100"
            />
          </div>

          <div>
            <Label htmlFor="bk-promo" className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500">
              Promotion Code (optional)
            </Label>
            <div className="flex gap-2 mt-1.5">
              <Input
                id="bk-promo"
                data-testid="promo-code-input"
                value={promoInput}
                onChange={(e) => {
                  setPromoInput(e.target.value.toUpperCase());
                  setPromo(null);
                  setPromoError("");
                }}
                placeholder="e.g. BM10"
                disabled={!!promo}
                className={`${inputCls} uppercase disabled:opacity-60`}
              />
              <Button
                type="button"
                data-testid="promo-apply-button"
                onClick={applyPromo}
                disabled={!promoInput.trim() || !!promo}
                variant="outline"
                className="border-[#262933] bg-transparent hover:bg-nitro/10 hover:border-nitro text-neutral-100 font-heading uppercase tracking-wider rounded-full px-5 disabled:opacity-50"
              >
                Apply
              </Button>
            </div>
            {promo && (
              <p data-testid="promo-applied" className="mt-2 font-mono text-xs text-emerald-400 flex items-center gap-1.5">
                <Tag size={12} />
                {promo.code}{promo.note ? ` — ${promo.note}` : ""} applied
              </p>
            )}
            {promoError && (
              <p data-testid="promo-error" className="mt-2 font-mono text-xs text-nitro">
                {promoError}
              </p>
            )}
          </div>

          {totalPrice != null && (
            <div
              data-testid="booking-price-summary"
              className="rounded-lg border border-[#262933] bg-[#0E1015] px-4 py-3 flex items-center justify-between gap-3"
            >
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500">
                Total — pay on the day
              </span>
              <span className="font-heading font-bold text-2xl text-neutral-100 flex items-baseline gap-2">
                {promo && totalPrice < basePrice && (
                  <span className="line-through text-neutral-500 text-base font-medium">£{basePrice}</span>
                )}
                £{totalPrice}
                {promo && totalPrice < basePrice && (
                  <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-emerald-400">
                    {promo.code} applied
                  </span>
                )}
              </span>
            </div>
          )}

          <Button
            data-testid="booking-submit-button"
            type="submit"
            disabled={sending}
            className="mt-2 bg-nitro hover:bg-[#ff4561] text-white font-heading font-semibold uppercase tracking-widest text-lg rounded-full py-6 transition-all duration-300 hover:shadow-[0_0_25px_rgba(255,46,77,0.45)] disabled:opacity-60"
          >
            {sending ? (
              <>
                <Loader2 className="mr-2 animate-spin" size={18} /> Sending…
              </>
            ) : (
              "Confirm Booking Request"
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
